import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { LoginForm } from '../components/admin/LoginForm';
import { Sidebar } from '../components/admin/Sidebar';
import { Dashboard } from '../components/admin/Dashboard';
import { PostsManager } from '../components/admin/PostsManager';
import { PagesManager } from '../components/admin/PagesManager';
import { MediaLibrary } from '../components/admin/MediaLibrary';
import { BookingsManager } from '../components/admin/BookingsManager';
import { MessagesManager } from '../components/admin/MessagesManager';
import { SEOManager } from '../components/admin/SEOManager';
import { TrackingManager } from '../components/admin/TrackingManager';
import { SettingsManager } from '../components/admin/SettingsManager';
import { getAuthHeader, API, axios } from '../components/admin/adminHelpers';

const AdminPanel = ({ auth, onLogout }) => {
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});

  const authHeader = getAuthHeader(auth.username, auth.password);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [bookings, messages, posts] = await Promise.all([
          axios.get(`${API}/admin/bookings`, { headers: authHeader }),
          axios.get(`${API}/admin/contacts`, { headers: authHeader }),
          axios.get(`${API}/admin/posts`, { headers: authHeader }).catch(() => ({ data: [] }))
        ]);
        setStats({
          bookings: bookings.data.length,
          messages: messages.data.length,
          posts: posts.data.length,
          pending: bookings.data.filter(b => b.status === 'pending' || !b.status).length
        });
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, [authHeader]);

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard': return <Dashboard stats={stats} setActiveMenu={setActiveMenu} />;
      case 'posts': return <PostsManager authHeader={authHeader} />;
      case 'pages': return <PagesManager authHeader={authHeader} />;
      case 'media': return <MediaLibrary authHeader={authHeader} />;
      case 'bookings': return <BookingsManager authHeader={authHeader} />;
      case 'messages': return <MessagesManager authHeader={authHeader} />;
      case 'seo': return <SEOManager authHeader={authHeader} />;
      case 'tracking': return <TrackingManager authHeader={authHeader} />;
      case 'settings': return <SettingsManager authHeader={authHeader} />;
      default: return <Dashboard stats={stats} setActiveMenu={setActiveMenu} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-panel">
      <Sidebar
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        onLogout={onLogout}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow z-30 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="text-gray-600" data-testid="mobile-menu-btn">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-semibold text-gray-900">13cabsbook Admin</span>
        <div className="w-6" />
      </div>
      <main className="lg:ml-64 p-4 lg:p-8 pt-16 lg:pt-8">
        {renderContent()}
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth) setAuth(JSON.parse(savedAuth));
  }, []);

  const handleLogin = (credentials) => {
    sessionStorage.setItem('adminAuth', JSON.stringify(credentials));
    setAuth(credentials);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setAuth(null);
  };

  if (!auth) return <LoginForm onLogin={handleLogin} />;
  return <AdminPanel auth={auth} onLogout={handleLogout} />;
};

export default AdminPage;
